import { useLayoutEffect } from "react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { useAuth } from "@/hooks/useAuth";
import { useBranding } from "@/hooks/useBranding";
import { LoadingScreen } from "@/components/layout/loading-screen";
import { AdminRoutes } from "@/components/routing/admin-routes";
import { ClientRoutes } from "@/components/routing/client-routes";
import { userHasPermission } from "@/lib/permissions";
import { toast } from "@/hooks/use-toast";
import LoginPage from "@/pages/login";
import AcceptInvitePage from "@/pages/accept-invite";
import RequestFormPage from "@/pages/request-form";
import SentryTestPage from "@/pages/sentry-test";
import NotFound from "@/pages/not-found";
import RiskScanPage from "@/pages/risk-scan";
import AuditSharePage from "@/pages/audit-share";
import {
  loginUrlForProtectedPath,
  mismatchRedirect,
} from "@shared/auth/safe-return-path";

const DENIED_PAGE_TOAST = "That page isn't available for this account.";

function DeniedRedirect({ to }: { to: string }) {
  const [, navigate] = useLocation();

  useLayoutEffect(() => {
    toast({ title: DENIED_PAGE_TOAST });
    navigate(to, { replace: true });
  }, [navigate, to]);

  return null;
}

function Router() {
  const { isLoading, isAuthenticated, user } = useAuth();
  const [pathname] = useLocation();
  useBranding();

  if (isLoading) return <LoadingScreen />;

  const isInternalUser =
    user?.userType === "admin" || user?.userType === "agent";

  if (!isAuthenticated) {
    // useSearch() runs decodeURI and strips "?". Keep the raw query on the return path.
    const loginUrl = loginUrlForProtectedPath(pathname, window.location.search);
    if (loginUrl) return <Redirect to={loginUrl} replace />;
  }

  if (isAuthenticated && user) {
    const mismatch = mismatchRedirect(user.userType, pathname);
    if (mismatch) return <DeniedRedirect to={mismatch} />;

    const filesDenied =
      isInternalUser &&
      (pathname === "/admin/files" || pathname.startsWith("/admin/files/")) &&
      !userHasPermission(user.effectivePermissions, "files.read");
    if (filesDenied) return <DeniedRedirect to="/admin" />;
  }

  return (
    <Switch>
      {/* Public routes — always accessible */}
      <Route path="/login" component={LoginPage} />
      <Route path="/accept-invite" component={AcceptInvitePage} />
      <Route path="/request" component={RequestFormPage} />
      <Route path="/sentry-test" component={SentryTestPage} />
      <Route path="/risk-scan/:token" component={RiskScanPage} />
      <Route path="/audit/:token" component={AuditSharePage} />

      {/* Admin routes — exact /admin + /admin/... subpaths */}
      {isAuthenticated && isInternalUser && (
        <Route path="/admin" nest component={AdminRoutes} />
      )}

      {/* Client portal routes — exact /portal + /portal/... subpaths */}
      {isAuthenticated && user?.userType === "client" && (
        <Route path="/portal" nest component={ClientRoutes} />
      )}

      {/* Root redirect based on role */}
      {isAuthenticated && isInternalUser && (
        <Route path="/">
          <Redirect to="/admin" />
        </Route>
      )}
      {isAuthenticated && user?.userType === "client" && (
        <Route path="/">
          <Redirect to="/portal" />
        </Route>
      )}

      {/* Unauthenticated fallback — land on the service request form */}
      {!isAuthenticated && (
        <Route>
          <Redirect to="/request" />
        </Route>
      )}

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <Router />
    </QueryClientProvider>
  );
}

export default App;
